import { useEffect, useState } from "react";

import { Link as ScrollLink } from "react-scroll";

import type { ImageType } from "~/components/common/images";
import Image from "~/components/common/images";
import KonnectButton from "~/components/common/KonnectButton/KonnectButton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { SCROLL_OFFSET } from "../Header/Header";
import PlayButton from "../PlayButton/PlayButton";

export interface IntroSectionProps {
  id: string;
  type: string;
  title: string;
  description: string;
  image?: ImageType;
  imageMobile?: ImageType;
  imageUpdate?: ImageType;
  button?: {
    text: string;
    url: string;
  };
  buttonPlay?: {
    text: string;
    url: string;
  };
  videoUrl?: string;
}

const getYouTubeVideoId = (value: string) => {
  try {
    const url = new URL(value);

    if (url.hostname.includes("youtu.be")) {
      return url.pathname.slice(1);
    }

    return url.searchParams.get("v") ?? url.pathname.split("/").pop() ?? value;
  } catch {
    return value;
  }
};

const IntroSection = (introSection: IntroSectionProps) => {
  const [videoOpen, setVideoOpen] = useState(false);
  const videoId = introSection.videoUrl
    ? getYouTubeVideoId(introSection.videoUrl)
    : "";
  const videoEmbedUrl = videoId
    ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`
    : "";

  useEffect(() => {
    if (videoOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [videoOpen]);

  return (
    <section id="intro" className="relative overflow-hidden lg:max-h-[1000px]">
      <div className="absolute bottom-0 left-1/2 z-[-1] w-[720px] translate-x-1/2 lg:bottom-auto lg:left-auto lg:right-[-5vh] lg:ml-auto lg:h-full lg:w-auto lg:translate-x-0">
        <svg
          viewBox="0 0 744 657"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="size-full"
        >
          <path
            d="M200.358 200H327C322.59 265.791 299.908 326.521 264.624 377.129C242.572 408.759 214.85 436.594 183.977 460C214.85 482.774 242.572 510.608 264.624 542.871C299.908 594.112 322.59 654.842 327 720H200.358C189.017 614.355 105.22 530.219 0 518.832V491.63V401.168C105.22 389.781 189.017 305.645 200.358 200Z"
            fill="#792BFF"
            fillOpacity="0.1"
          />
          <path
            d="M543.642 0H417C421.41 65.7908 444.092 126.521 479.376 177.129C501.428 208.759 529.15 236.594 560.023 260C529.15 282.774 501.428 310.608 479.376 342.871C444.092 394.112 421.41 454.842 417 520H543.642C554.983 414.355 638.78 330.219 744 318.832V291.63V201.168C638.78 189.781 554.983 105.645 543.642 0Z"
            fill="#BDF5D0"
            fillOpacity="0.45"
          />
        </svg>
      </div>
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        {videoEmbedUrl && (
          <DialogContent className="w-[min(calc(100vw-2rem),calc((100dvh-6rem)*9/16))] max-w-sm overflow-hidden border-0 bg-black p-0 text-white shadow-none sm:w-[calc(100vw-2rem)] sm:max-w-5xl [&>button]:z-10 [&>button]:rounded-full [&>button]:bg-black/60 [&>button]:p-2 [&>button]:text-white [&>button]:opacity-100">
            <DialogTitle className="sr-only">Konnect app video</DialogTitle>
            <div className="aspect-[9/16] w-full bg-black sm:aspect-video">
              <iframe
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="size-full"
                referrerPolicy="strict-origin-when-cross-origin"
                src={videoEmbedUrl}
                title="Konnect app video"
              />
            </div>
          </DialogContent>
        )}
      </Dialog>
      <div className="section-padding-x relative flex h-auto flex-col items-start overflow-hidden pt-[6vh] lg:h-[85vh] lg:max-h-[880px] lg:flex-row lg:items-center lg:pt-0">
        <div className="relative z-[1] w-full lg:w-1/2">
          <h1 className="h1 mb-[35px] pr-[15%] font-semibold text-charcoal lg:pr-0">
            {introSection.title}
          </h1>
          <p className="mb-[45px] max-w-[85%] text-lg font-light lg:text-xl">
            {introSection.description}
          </p>
          <div className="mt-[45px] flex flex-col gap-[20px] md:max-w-[50%] lg:max-w-none lg:flex-row">
            {introSection.button && (
              <ScrollLink
                to={"PricingSection"}
                smooth={true}
                offset={-SCROLL_OFFSET}
                duration={500}
              >
                <KonnectButton
                  icon={true}
                  text={introSection.button.text}
                  bgColor="charcoal"
                  textColor="white"
                />
              </ScrollLink>
            )}
            {introSection.buttonPlay && introSection?.videoUrl && (
              <KonnectButton
                icon={true}
                text={introSection.buttonPlay.text}
                bgColor="transparent"
                hoverBgColor="transparent"
                textColor="charcoal"
                hoverTextColor="purple"
                onClick={() => {
                  setVideoOpen(true);
                }}
                className="justify-start text-left md:justify-center"
              />
            )}
          </div>
          <div className="mt-[15vh] hidden lg:block">
            <ScrollLink
              to={"FeatureSection"}
              smooth={true}
              offset={-SCROLL_OFFSET}
              duration={500}
              className="mt-[15px] flex cursor-pointer text-sm leading-normal text-purple transition hover:text-charcoal"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.5835 9L11.2161 13.8996C11.1877 13.9314 11.1541 13.9567 11.117 13.9739C11.08 13.9911 11.0403 14 11.0002 14C10.9601 14 10.9203 13.9911 10.8833 13.9739C10.8462 13.9567 10.8126 13.9314 10.7842 13.8996L6.41683 9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <rect
                  x="0.75"
                  y="0.75"
                  width="20.5"
                  height="20.5"
                  rx="10.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
              <span className="ml-[13px]">Discover features</span>
            </ScrollLink>
          </div>
        </div>
        <div className="m-auto mt-10 w-full max-w-[400px] lg:mt-0 lg:h-[80vh] lg:w-1/2 lg:max-w-none lg:py-[15px] lg:pl-[10%]">
          <div className="relative flex size-full items-center justify-center">
            {introSection.imageUpdate && (
              <Image
                image={introSection.imageUpdate}
                alt={"Konnect app screenshot"}
                className="size-auto max-h-full max-w-full"
                loading="eager"
              />
            )}
            {introSection?.videoUrl && (
              <PlayButton
                className="absolute mt-[-10%]"
                onClick={() => {
                  setVideoOpen(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntroSection;
